import { useActiveProject } from '@hooks/useActiveProject';
import { useGstRate } from '@hooks/useGstRate';
import { useRazorpay } from '@hooks/useRazorpay';
import { useReactivateProject } from '@hooks/useReactivateProject';
import { gstOn, withGst } from '@lib/gst';
import type { WalletStatus } from '@services/wallet.service';
import { useAuthStore } from '@stores/auth.store';
import { Button, Card, Input } from '@ui/index';
import { Lock, LogOut, MessageSquare, PlayCircle, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const INR_FMT = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

/** 2dp variant for GST/payable figures, which are rarely whole rupees. */
const INR_FMT_PAISE = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmtINRPaise(n: number): string {
  return INR_FMT_PAISE.format(Math.max(0, n));
}

function fmtINR(n: number): string {
  return INR_FMT.format(Math.max(0, n));
}

/** Like {@link fmtINR} but preserves the real value, including an overdrawn
 *  (negative) balance — so the blocker can show *why* the minimum is what it
 *  is, instead of flooring the balance to ₹0. */
function fmtINRSigned(n: number): string {
  return INR_FMT.format(n);
}

export interface WalletBlockerOverlayProps {
  status: WalletStatus;
}

/**
 * Full-workspace blocker. Renders a blurred, non-interactive scrim over the
 * entire app shell when the wallet gate state is `blocked` (balance ≤ 0 or a
 * paused project). Navigation underneath is unreachable — the only action is
 * to recharge.
 *
 * The recharge amount is pre-filled with — and floored at — the server's
 * `required_recharge_inr` (negative balance + plan fees), mirroring the
 * backend's `RECHARGE_BELOW_MINIMUM` enforcement so the project doesn't
 * immediately re-block on resume. Inbound stays live; the unread count is
 * shown to nudge the recharge.
 */
export function WalletBlockerOverlay({ status }: WalletBlockerOverlayProps): JSX.Element {
  const { initiate, isLoading } = useRazorpay();
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { selectedProjectId } = useActiveProject();
  const resume = useReactivateProject(selectedProjectId);

  const handleLogout = (): void => {
    clearAuth();
    navigate('/login', { replace: true });
  };

  // A paused project whose wallet already covers its fees owes nothing — the
  // server reports `required_recharge_inr = 0`. Demanding another payment
  // there is wrong (and the floor below would invent a ₹100 minimum), so the
  // action becomes "resume" instead. Without this the overlay is a trap: it
  // covers the whole workspace, so every other resume surface sits underneath
  // this scrim and is unreachable.
  const nothingOwed = status.required_recharge_inr <= 0;

  // Floor is the required recharge, but never below ₹100 (Razorpay order min).
  const minAmount = useMemo(
    () => Math.max(100, Math.ceil(status.required_recharge_inr)),
    [status.required_recharge_inr],
  );
  const [amount, setAmount] = useState<number>(minAmount);

  // Keep the input in step with a changing requirement (e.g. realtime debit).
  useEffect(() => {
    setAmount((prev) => (prev < minAmount ? minAmount : prev));
  }, [minAmount]);

  const belowMin = amount < minAmount;
  // GST is charged ON TOP at checkout: the wallet receives `amount`, the
  // customer pays `payable`. Surfacing it here prevents a surprise at Razorpay.
  const gstRatePct = useGstRate();
  const gstAmount = gstOn(Number.isFinite(amount) ? amount : 0, gstRatePct);
  const payable = withGst(Number.isFinite(amount) ? amount : 0, gstRatePct);

  return (
    <div
      // The scroll lives on this container, with the card centred by a
      // min-h-full wrapper rather than `items-center`. Centring a flex item
      // that overflows makes its top unreachable, and this card is the only
      // way out of a blocked workspace — an unreachable Pay button would
      // strand the account. The scrim is fixed so it still covers the
      // viewport once the container scrolls.
      className="fixed inset-0 z-[60] overflow-y-auto p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wallet-blocker-title"
    >
      {/* Blurred scrim over the whole workspace. */}
      <div className="fixed inset-0 bg-white/55 backdrop-blur-md" aria-hidden="true" />

      <div className="flex min-h-full items-center justify-center">
        <Card className="relative z-10 w-full max-w-md" padding="lg">
        <div className="flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
            <Lock size={22} strokeWidth={2} />
          </div>

          <h2
            id="wallet-blocker-title"
            className="mt-4 text-[19px] font-semibold text-apple-label"
          >
            {status.balance < 0
              ? 'Service paused — wallet overdrawn'
              : status.balance === 0
                ? 'Service paused — wallet empty'
                : 'Service paused'}
          </h2>
          <p className="mt-1.5 text-[13px] leading-relaxed text-apple-secondary">
            {status.balance <= 0 ? (
              <>
                Sending is paused while your wallet balance is{' '}
                <span className="font-semibold">{fmtINRSigned(status.balance)}</span>.{' '}
              </>
            ) : (
              <>Outbound sending is paused for this number. </>
            )}
            {nothingOwed
              ? 'Your wallet covers the plan, so you can resume right away.'
              : 'Recharge to resume outbound messaging and billing.'}{' '}
            Incoming messages are still being received.
          </p>

          {status.unread_count > 0 && (
            <div className="mt-4 flex items-center gap-2 rounded-xl bg-amber-50 px-3.5 py-2 text-[13px] text-amber-800">
              <MessageSquare size={15} strokeWidth={2} className="shrink-0" />
              <span>
                <span className="font-semibold tabular-nums">{status.unread_count}</span>{' '}
                unread {status.unread_count === 1 ? 'message is' : 'messages are'} waiting
                for a reply.
              </span>
            </div>
          )}

          <div className="mt-5 w-full rounded-2xl bg-apple-gray-100 px-4 py-3 text-left">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium uppercase tracking-wider text-apple-secondary">
                Current balance
              </span>
              <span
                className={`text-[14px] font-semibold tabular-nums ${
                  status.balance < 0 ? 'text-rose-600' : 'text-apple-label'
                }`}
              >
                {fmtINRSigned(status.balance)}
              </span>
            </div>
            {!nothingOwed && (
              <div className="mt-2 flex items-end justify-between border-t border-black/5 pt-2.5">
                <span className="text-[11px] font-medium uppercase tracking-wider text-apple-secondary">
                  Minimum to resume
                </span>
                <span className="text-[22px] font-semibold leading-none tabular-nums text-apple-label">
                  {fmtINR(minAmount)}
                </span>
              </div>
            )}
            {status.balance < 0 && (
              <p className="mt-2.5 text-[11.5px] leading-snug text-apple-secondary">
                Clears your {fmtINRSigned(status.balance)} overdraft and funds the
                upcoming plan renewal so service doesn’t immediately pause again.
              </p>
            )}
          </div>

          <div className={nothingOwed ? 'hidden' : 'mt-4 w-full text-left'}>
            <label
              htmlFor="wallet-blocker-amount"
              className="text-[12px] font-medium text-apple-secondary"
            >
              Recharge amount (₹)
            </label>
            <Input
              id="wallet-blocker-amount"
              type="number"
              min={minAmount}
              step={100}
              value={Number.isFinite(amount) ? amount : ''}
              onChange={(e) => setAmount(Math.floor(Number(e.target.value) || 0))}
              className="mt-1"
            />
            {belowMin && (
              <p className="mt-1 text-[12px] text-rose-600">
                Enter at least {fmtINR(minAmount)} to resume service.
              </p>
            )}
            {!belowMin && (
              <div className="mt-2 flex items-center justify-between rounded-xl bg-apple-gray-100 px-3.5 py-2 text-[12px] text-apple-secondary">
                <span>
                  Wallet credit {fmtINR(amount)} + GST ({gstRatePct}%){' '}
                  {fmtINRPaise(gstAmount)}
                </span>
                <span className="font-semibold tabular-nums text-apple-label">
                  {fmtINRPaise(payable)}
                </span>
              </div>
            )}
          </div>

          {nothingOwed ? (
            <>
              <Button
                variant="primary"
                size="lg"
                className="mt-5 w-full"
                disabled={resume.isPending}
                onClick={() => resume.mutate()}
              >
                <PlayCircle size={16} strokeWidth={2} className="mr-2" />
                {resume.isPending ? 'Resuming…' : 'Resume service'}
              </Button>
              <p className="mt-2 text-[11.5px] text-apple-secondary">
                Your wallet already covers this number’s plan — no payment needed.
              </p>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                size="lg"
                className="mt-5 w-full"
                disabled={isLoading || belowMin}
                onClick={() => void initiate(amount)}
              >
                <Wallet size={16} strokeWidth={2} className="mr-2" />
                {isLoading ? 'Opening checkout…' : `Pay ${fmtINRPaise(payable)}`}
              </Button>
              <p className="mt-2 text-[11.5px] text-apple-secondary">
                {fmtINR(amount)} lands in your wallet; GST is billed on top with a tax
                invoice emailed to you.
              </p>
            </>
          )}

          {/* The scrim sits above the TopBar (z-60 vs z-30), so its account menu
              — and the only sign-out in the shell — is unreachable while this is
              up. Without this the overlay is a dead end for anyone who needs to
              switch accounts rather than pay. */}
          <div className="mt-5 w-full border-t border-black/5 pt-4">
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-apple-secondary transition-colors hover:text-apple-label"
            >
              <LogOut size={14} strokeWidth={2} />
              Sign out
            </button>
          </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default WalletBlockerOverlay;
